import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Upload, ExternalLink, CheckCircle2, AlertCircle, Loader2, 
  Package, Link2, Search, ArrowRight, Sparkles, ShoppingBag
} from "lucide-react";
import api from "@/utils/api";

// eBay logo SVG
const EbayLogo = () => (
  <svg viewBox="0 0 100 40" className="h-8 w-auto">
    <text x="0" y="30" fill="#E53238" fontWeight="bold" fontSize="28">e</text>
    <text x="18" y="30" fill="#0064D2" fontWeight="bold" fontSize="28">b</text>
    <text x="38" y="30" fill="#F5AF02" fontWeight="bold" fontSize="28">a</text>
    <text x="58" y="30" fill="#86B817" fontWeight="bold" fontSize="28">y</text>
  </svg>
);

const ImportFromEbay = ({ user }) => {
  const [activeTab, setActiveTab] = useState("url");
  const [loading, setLoading] = useState(false);
  const [demoMode, setDemoMode] = useState(true);
  const [connected, setConnected] = useState(false);
  const [ebayUsername, setEbayUsername] = useState("");
  const [listings, setListings] = useState([]);
  const [selectedListings, setSelectedListings] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [ebayUrl, setEbayUrl] = useState("");
  const [urlImporting, setUrlImporting] = useState(false);
  const [importedListings, setImportedListings] = useState([]);

  // Check eBay integration status
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await api.get('/ebay/status');
        setDemoMode(response.data.demo_mode);
        
        // If not in demo mode, check connection status
        if (!response.data.demo_mode) {
          const connResponse = await api.get('/ebay/connection-status');
          if (connResponse.data.connected) {
            setConnected(true);
            setEbayUsername(connResponse.data.ebay_username);
          }
        }
      } catch (error) {
        console.log("eBay status check failed, defaulting to demo mode");
        setDemoMode(true);
      }
    };
    checkStatus();
  }, []);

  // Fetch demo listings when in demo mode
  useEffect(() => {
    if (demoMode && activeTab === "account") {
      fetchDemoListings();
    }
  }, [demoMode, activeTab]);

  const fetchDemoListings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/ebay/demo-listings');
      setListings(response.data.listings || []);
      toast.success(`Found ${response.data.listings.length} demo listings!`);
    } catch (error) {
      toast.error("Failed to load demo listings");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectEbay = async () => {
    if (demoMode) {
      toast.info("Demo mode active - eBay account connection is simulated");
      setConnected(true);
      setEbayUsername("demo_seller");
      await fetchDemoListings();
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.get('/ebay/auth-url');
      window.open(response.data.auth_url, 'ebay_auth', 'width=600,height=700');
      
      // Listen for callback
      const checkConnection = setInterval(async () => {
        try {
          const status = await api.get('/ebay/connection-status');
          if (status.data.connected) {
            clearInterval(checkConnection);
            setConnected(true);
            setEbayUsername(status.data.ebay_username);
            toast.success("eBay account connected!");
          }
        } catch (error) { }
      }, 2000);
      
      setTimeout(() => clearInterval(checkConnection), 300000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to connect to eBay");
    } finally {
      setLoading(false);
    }
  };

  const handleFetchListings = async () => {
    setLoading(true);
    try {
      const endpoint = demoMode ? '/ebay/demo-listings' : '/ebay/my-listings';
      const response = await api.get(endpoint);
      setListings(response.data.listings || []);
      if (response.data.listings?.length === 0) {
        toast.info("No listings found");
      } else {
        toast.success(`Found ${response.data.listings.length} listings!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch listings");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectListing = (listingId) => {
    setSelectedListings(prev => 
      prev.includes(listingId) 
        ? prev.filter(id => id !== listingId)
        : [...prev, listingId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedListings.length === filteredListings.length) {
      setSelectedListings([]);
    } else {
      setSelectedListings(filteredListings.map(l => l.itemId));
    }
  };

  const handleBulkImport = async () => {
    if (selectedListings.length === 0) {
      toast.error("Please select at least one listing to import");
      return;
    }

    setImporting(true);
    setImportProgress(0);

    try {
      const listingsToImport = listings.filter(l => selectedListings.includes(l.itemId));
      let imported = 0;
      const newImported = [];

      for (const listing of listingsToImport) {
        try {
          const endpoint = demoMode ? '/ebay/import-demo' : '/ebay/import-listing';
          const result = await api.post(endpoint, { ebay_item_id: listing.itemId });
          imported++;
          newImported.push({
            ...listing,
            bidzuu_id: result.data.listing_id
          });
          setImportProgress(Math.round((imported / listingsToImport.length) * 100));
        } catch (error) {
          console.error(`Failed to import ${listing.title}:`, error);
        }
      }

      setImportedListings(prev => [...prev, ...newImported]);
      toast.success(`Successfully imported ${imported} listings to BidZuu!`);
      setSelectedListings([]);
    } catch (error) {
      toast.error("Import failed. Please try again.");
    } finally {
      setImporting(false);
    }
  };

  const handleUrlImport = async (e) => {
    e.preventDefault();
    if (!ebayUrl.trim()) {
      toast.error("Please enter an eBay listing URL");
      return;
    }

    // Validate URL format
    if (!ebayUrl.includes('ebay.com/itm') && !ebayUrl.includes('ebay.co.uk/itm')) {
      toast.error("Please enter a valid eBay item URL");
      return;
    }

    setUrlImporting(true);
    try {
      const response = await api.post('/ebay/import-from-url', { ebay_url: ebayUrl });
      toast.success(`Imported: ${response.data.title}`);
      setImportedListings(prev => [...prev, {
        itemId: response.data.listing_id,
        title: response.data.title,
        price: response.data.price,
        bidzuu_id: response.data.listing_id
      }]);
      setEbayUrl("");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to import listing";
      if (errorMsg.includes("blocking") || errorMsg.includes("Could not fetch")) {
        toast.error(
          <div>
            <p className="font-medium">Unable to fetch eBay listing</p>
            <p className="text-sm mt-1">eBay is blocking automated access. Use the Demo Listings tab to test, or create your listing manually.</p>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setUrlImporting(false);
    }
  };

  const filteredListings = listings.filter(l => 
    l.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.itemId?.includes(searchQuery)
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <ShoppingBag className="h-6 w-6 text-emerald-600" />
            </div>
            <ArrowRight className="h-5 w-5 text-slate-400" />
            <EbayLogo />
          </div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="import-page-title">
            Import from eBay
          </h1>
          <p className="text-muted-foreground mt-2">
            Easily bring your eBay inventory to BidZuu
          </p>
          
          {/* Demo Mode Badge */}
          {demoMode && (
            <Badge variant="secondary" className="mt-3 bg-amber-100 text-amber-800 border-amber-200">
              <Sparkles className="h-3 w-3 mr-1" />
              Demo Mode - Using sample listings
            </Badge>
          )}
        </div>

        {/* Import Methods Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="url" data-testid="tab-url-import">
              <Link2 className="h-4 w-4 mr-2" />
              Import by URL
            </TabsTrigger>
            <TabsTrigger value="account" data-testid="tab-account-import">
              <Package className="h-4 w-4 mr-2" />
              {demoMode ? "Demo Listings" : "My eBay Account"}
            </TabsTrigger>
          </TabsList>

          {/* URL Import Tab */}
          <TabsContent value="url">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="h-5 w-5 text-emerald-600" />
                  Import by eBay URL
                </CardTitle>
                <CardDescription>
                  Paste an eBay listing URL to import it directly to your BidZuu store
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUrlImport} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="ebay-url">eBay Listing URL</Label>
                    <div className="flex gap-2">
                      <Input
                        id="ebay-url"
                        data-testid="ebay-url-input"
                        placeholder="https://www.ebay.com/itm/123456789"
                        value={ebayUrl}
                        onChange={(e) => setEbayUrl(e.target.value)}
                        className="flex-1"
                      />
                      <Button 
                        type="submit" 
                        disabled={urlImporting || !ebayUrl.trim()}
                        data-testid="import-url-button"
                      >
                        {urlImporting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Import
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                    <h4 className="font-medium text-blue-900 mb-2">Supported URL formats:</h4>
                    <ul className="text-blue-800 space-y-1">
                      <li>• https://www.ebay.com/itm/123456789</li>
                      <li>• https://www.ebay.com/itm/item-title/123456789</li>
                      <li>• https://www.ebay.co.uk/itm/123456789</li>
                    </ul>
                  </div>
                  
                  {/* Notice about eBay blocking */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-amber-900">Note about URL imports</h4>
                        <p className="text-amber-800 mt-1">
                          eBay may block automated access to some listings. If import fails, try the 
                          <button 
                            type="button"
                            onClick={() => setActiveTab("account")} 
                            className="text-amber-900 underline mx-1 font-medium"
                          >
                            Demo Listings
                          </button>
                          tab to test the feature, or create your listing manually on BidZuu.
                        </p>
                      </div>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Account Import Tab */}
          <TabsContent value="account">
            {!connected && !demoMode ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ExternalLink className="h-5 w-5" />
                    Connect Your eBay Account
                  </CardTitle>
                  <CardDescription>
                    Authorize BidZuu to access your eBay listings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">What we access:</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-blue-600" />
                        Your listing titles, descriptions, and prices
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-blue-600" />
                        Product images from your listings
                      </li>
                    </ul>
                  </div>

                  <Button 
                    onClick={handleConnectEbay} 
                    disabled={loading}
                    className="w-full bg-[#0064D2] hover:bg-[#0050A8]"
                    size="lg"
                    data-testid="connect-ebay-button"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : (
                      <ExternalLink className="h-5 w-5 mr-2" />
                    )}
                    Connect eBay Account
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    {demoMode ? "Demo Listings" : "Your eBay Listings"}
                  </CardTitle>
                  <CardDescription>
                    {demoMode 
                      ? "Sample listings for testing the import flow" 
                      : `Connected as ${ebayUsername}`
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Search and Actions */}
                  <div className="flex items-center gap-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search listings..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="listing-search-input"
                      />
                    </div>
                    {listings.length === 0 ? (
                      <Button onClick={handleFetchListings} disabled={loading}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Load Listings
                      </Button>
                    ) : (
                      <Button variant="outline" onClick={toggleSelectAll}>
                        {selectedListings.length === filteredListings.length ? 'Deselect All' : 'Select All'}
                      </Button>
                    )}
                  </div>

                  {/* Listing Count */}
                  {listings.length > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {filteredListings.length} listings
                      </span>
                      <Badge variant="secondary">
                        {selectedListings.length} selected
                      </Badge>
                    </div>
                  )}

                  {/* Listings Grid */}
                  {listings.length > 0 && (
                    <ScrollArea className="h-[400px] pr-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filteredListings.map((listing) => (
                          <div
                            key={listing.itemId}
                            onClick={() => toggleSelectListing(listing.itemId)}
                            className={`border rounded-lg p-3 cursor-pointer transition-all ${
                              selectedListings.includes(listing.itemId)
                                ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            data-testid={`listing-card-${listing.itemId}`}
                          >
                            <div className="flex gap-3">
                              <div className="relative">
                                <img
                                  src={listing.image || listing.images?.[0] || '/placeholder.png'}
                                  alt={listing.title}
                                  className="w-20 h-20 object-cover rounded-lg bg-slate-100"
                                  onError={(e) => e.target.src = 'https://via.placeholder.com/80x80?text=No+Image'}
                                />
                                {selectedListings.includes(listing.itemId) && (
                                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                                    <CheckCircle2 className="h-4 w-4 text-white" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-sm line-clamp-2">{listing.title}</h4>
                                <p className="text-lg font-bold text-emerald-600 mt-1">
                                  ${listing.price?.toFixed(2)}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    {listing.condition || 'Used'}
                                  </Badge>
                                  {listing.quantity > 1 && (
                                    <span className="text-xs text-muted-foreground">
                                      Qty: {listing.quantity}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}

                  {/* Import Button */}
                  {listings.length > 0 && (
                    <div className="flex gap-4 pt-4 border-t">
                      <Button 
                        onClick={handleBulkImport} 
                        disabled={importing || selectedListings.length === 0}
                        className="flex-1"
                        data-testid="bulk-import-button"
                      >
                        {importing ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Importing... {importProgress}%
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Import {selectedListings.length} Listings
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Progress Bar */}
                  {importing && (
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${importProgress}%` }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Imported Listings Summary */}
        {importedListings.length > 0 && (
          <Card className="mt-6 border-emerald-200 bg-emerald-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-5 w-5" />
                Successfully Imported ({importedListings.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {importedListings.slice(-5).map((listing, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg">
                    <span className="font-medium text-sm line-clamp-1">{listing.title}</span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => window.open(`/item/${listing.bidzuu_id}`, '_blank')}
                    >
                      View
                    </Button>
                  </div>
                ))}
              </div>
              {importedListings.length > 5 && (
                <p className="text-sm text-muted-foreground mt-3 text-center">
                  +{importedListings.length - 5} more listings imported
                </p>
              )}
              <div className="mt-4 flex justify-center">
                <Button onClick={() => window.location.href = '/account?tab=listings'}>
                  View All My Listings
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Section */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>Having trouble? <a href="/help" className="text-emerald-600 hover:underline">Contact Support</a></p>
        </div>
      </div>
    </div>
  );
};

export default ImportFromEbay;
